import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Section from './Section';
import UssdInput from './UssdInput';
import { LiveDot } from './UssdPanel';
import askSuggestions from '../data/ask';
import { useSiteChat } from '../contexts/ChatContext';
import {
  REACT_APP_SOCIAL_FACEBOOK,
  REACT_APP_SOCIAL_GITHUB,
  REACT_APP_SOCIAL_LINKEDIN,
  REACT_APP_SOCIAL_TWITTER,
} from '../env';

const SOCIALS = [
  { label: 'GitHub', href: REACT_APP_SOCIAL_GITHUB },
  { label: 'LinkedIn', href: REACT_APP_SOCIAL_LINKEDIN },
  { label: 'Twitter', href: REACT_APP_SOCIAL_TWITTER },
  { label: 'Facebook', href: REACT_APP_SOCIAL_FACEBOOK },
].filter((item) => Boolean(item.href));

const openers = askSuggestions.slice(0, 3);

/**
 * The last block before the footer. A nested USSD session — which is exactly
 * what a real handset does when you pick a submenu — where the numbered options
 * are questions rather than pages, and answering opens the assistant.
 */
function AskSection() {
  const { ask } = useSiteChat();
  const [armed, setArmed] = useState(false);
  const regionRef = useRef(null);

  useEffect(() => {
    if (!armed) return undefined;

    const onKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target && event.target.tagName === 'INPUT') return;
      const index = Number(event.key) - 1;
      if (Number.isNaN(index) || !openers[index]) return;
      event.preventDefault();
      ask(openers[index]);
    };

    const node = regionRef.current;
    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
  }, [armed, ask]);

  return (
    <Section
      id="ask"
      number="0"
      title="Ask me anything"
      aside={
        <span className="flex items-center gap-1.5">
          <LiveDot />
          Live
        </span>
      }
    >
      <div className="grid gap-8 py-10 grid-cols-1 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-8">
        <p className="label pt-2">Assistant</p>

        <div className="space-y-8">
          <p className="max-w-prose text-deck">
            I built an assistant on BuniAI and pointed it at ten years of my work. It
            answers in your words, not mine &mdash; so ask it the thing you actually
            came here to find out.
          </p>

          <div
            ref={regionRef}
            tabIndex={-1}
            onFocus={() => setArmed(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setArmed(false);
            }}
            onMouseEnter={() => setArmed(true)}
            onMouseLeave={() => setArmed(false)}
            className="max-w-2xl rounded-sm border border-black/40 bg-panel p-6 font-mono text-sm text-panel-foreground dark:border-white/10 sm:p-8"
          >
            <div className="flex items-center justify-between text-eyebrow uppercase tracking-[0.18em] text-panel-muted">
              <span>Assistant session</span>
              <span className="flex items-center gap-1.5">
                <LiveDot />
                Ready
              </span>
            </div>

            <p className="mt-5 leading-relaxed">
              <span className="text-panel-signal">CON</span>{'  '}Pick a question, or write your own.
            </p>

            <ul className="mt-5 space-y-1">
              {openers.map((question, i) => (
                <li key={question}>
                  <button
                    type="button"
                    onClick={() => ask(question)}
                    className="group flex w-[calc(100%+1rem)] items-baseline gap-3 -mx-2 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-white/[0.07] focus-visible:bg-white/[0.07]"
                  >
                    <span className="tabular-nums text-panel-signal">{i + 1}.</span>
                    <span>{question}</span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-6 border-t border-white/10 pt-4">
              <UssdInput onSubmit={ask} label="ask" placeholder="Ask your own question" />
            </div>
          </div>

          <div className="space-y-3">
            <p className="label">Or reach me the slow way</p>
            <ul className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
              {SOCIALS.map((item) => (
                <li key={item.label}>
                  <a href={item.href} target="_blank" rel="noopener noreferrer" className="link-rule">
                    {item.label}
                  </a>
                </li>
              ))}
              <li><Link to="/about" className="link-rule">Full background</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </Section>
  );
}

export default AskSection;
